import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  MultiSelect,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  useCompanies,
  useFormKayitAc,
  useIzinSecenekleri,
  useIzinTalebiAc,
  useIzinTuruFormlari,
  useWorkplacesTumu,
  useSahaGunu,
} from '../api/queries';
import type { PtwKisiAdayi, PtwTypeForm } from '@isg/shared-types';
import { IzinTuruFormGirisleri } from './izin-form-parts';
import { kisiEtiketi } from './izin-parts';
import {
  digerEksigiMetni,
  digerEksikleri,
  gonderilecekDigerMetinleri,
  semayiCoz,
} from './form-parts';
import type { CevapHaritasi, DigerMetinleri } from './form-parts';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { datetimeLocalDegeri } from '../lib/format';

/**
 * YENİ İZİN TALEBİ — ofis işi (F3·S4; Kapsam §16.7.4).
 *
 * ÇEKİRDEK ALANLAR: izin türü · lokasyon · geçerlilik · katılımcılar
 * (kişi + GÖREV) · ekipman · güncel sorumlu. Tehlike/önlem/KKD gibi TÜRE
 * ÖZEL form alanları BURADA YOKTUR ve elle EKLENMEZ: onlar form motorunun
 * (Kapsam §22.5) işidir ve S6'da şablon üzerinden gelir — aşağıdaki yer
 * tutucu bölge o alanların yerini işaretler. Bu sınırı delip alan eklemek,
 * motorun "şablonu biz veririz, müşteri düzenler" kuralını atlar.
 *
 * HAVUZLAR SUNUCUDAN: katılımcı/sorumlu havuzu bahçe kapsamından, ekipman
 * havuzu 1 GÜN MARJINDAN geçer. Marjı geçmeyen ekipman listede HİÇ YOKTUR
 * (griye alınmaz) — "seçilemez" kuralının UI karşılığı budur.
 */
export function IzinTalepScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const sirketler = useCompanies();
  const [companyId, setCompanyId] = useState<string | null>(null);
  // ✏️ TUR 9 / B4 (T9-18): şirket seçilince ayrı bir istek atmak yerine
  // bahçenin TÜM işyerleri tek istekte gelir ve seçime göre süzülür —
  // ⚠️ K12: seçici yalnız AKTİF işyerlerini gösterir (arşivliye yeni izin
  // talebi açılamaz; çalışamayacak kontrol çizilmez).
  const isyeriSorgusu = useWorkplacesTumu({ durum: 'aktif' });
  const isyerleri = (isyeriSorgusu.data ?? []).filter((w) => w.companyId === companyId);
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  // ✏️ TUR 10 / K2: kişi havuzları VARSAYILAN OLARAK iznin işyeriyle sınırlı.
  // Kutu, destek ekibi çağırma hâlini açık bırakır — süzgeç SUNUCUDA, ekran
  // ikinci filtre yazmaz (§2.16).
  const [tumHolding, setTumHolding] = useState(false);
  const secenekler = useIzinSecenekleri(workplaceId, tumHolding);
  const talepAc = useIzinTalebiAc();

  const [locationId, setLocationId] = useState<string | null>(null);
  const [baslangic, setBaslangic] = useState(varsayilanBaslangic());
  const [bitis, setBitis] = useState(varsayilanBitis());
  const [turIds, setTurIds] = useState<string[]>([]);
  const [ekipmanIds, setEkipmanIds] = useState<string[]>([]);
  const [sorumluId, setSorumluId] = useState<string | null>(null);
  const [katilimcilar, setKatilimcilar] = useState<{ personId: string; gorevRefId: string }[]>([]);
  const [yeniKisi, setYeniKisi] = useState<string | null>(null);
  const [yeniGorev, setYeniGorev] = useState<string | null>(null);
  // S8-1 — TÜR FORMU TALEPTE DOLAR: cevaplar şablon başına burada birikir,
  // "Taslağı kaydet" izinle birlikte form kayıtlarını da (taslak) yazar.
  const turFormlari = useIzinTuruFormlari(turIds);
  const formKayitAc = useFormKayitAc();
  // Madde 302: tür formu taslağının doldurma tarihi SAHA GÜNÜ (sunucudan).
  const sahaGunu = useSahaGunu();
  const [formCevaplari, setFormCevaplari] = useState<Record<string, CevapHaritasi>>({});
  // K-D1 — "Diğer" metinleri, cevaplarla aynı anahtarla (şablon başına).
  const [formDigerleri, setFormDigerleri] = useState<Record<string, DigerMetinleri>>({});

  const veri = secenekler.data;
  // ✏️ K-95c (bulgu 95): talep edenin bu işyerindeki istihdamı VARSAYILAN
  // seçili gelir — kolaylık, kural değil (talep eden sahada olmak zorunda
  // değildir; seçim değiştirilebilir). Değer TÜRETİLİR, efektle yazılmaz:
  // kullanıcının seçimi hiçbir zaman geri alınmaz (tur 8 / F5 dersi).
  const etkinSorumluId = sorumluId ?? veri?.varsayilanSorumluEmploymentId ?? null;
  // ✏️ KA-8 (bulgu 66): TASLAK MİNİMUMU işyeri + lokasyon (geçerlilik aralığı
  // ön dolu gelir; sunucu şeması `ptwPermitCreateSchema` aynı dördünü ister).
  // Eksik alan pasif düğmenin YANINDA adıyla söylenir — sebepsiz pasiflik yok.
  const taslakEksikleri = [
    workplaceId === null ? 'işyeri' : null,
    locationId === null ? 'lokasyon' : null,
    baslangic === '' ? 'başlangıç zamanı' : null,
    bitis === '' ? 'bitiş zamanı' : null,
  ].filter((ad): ad is string => ad !== null);
  // K-D1 — tür formunda "Diğer" seçilip açıklaması yazılmamışsa taslak
  // KAYDEDİLMEZ: form kaydı sunucuda reddedilir ve bu ekran o reddi yutar
  // (yolculuk durmasın diye) — kullanıcının seçimleri sessizce kaybolurdu.
  const digerEksigi = digerEksigiMetni(
    (turFormlari.data ?? []).flatMap((form) => {
      const sema = semayiCoz(form.sema);
      return sema
        ? digerEksikleri(
            sema,
            formCevaplari[form.formTemplateId] ?? {},
            formDigerleri[form.formTemplateId] ?? {},
          )
        : [];
    }),
  );
  const eksik = taslakEksikleri.length > 0 || digerEksigi !== null;
  const digerGovdesi = (form: PtwTypeForm): DigerMetinleri => {
    const sema = semayiCoz(form.sema);
    return sema
      ? gonderilecekDigerMetinleri(
          sema,
          formCevaplari[form.formTemplateId] ?? {},
          formDigerleri[form.formTemplateId] ?? {},
        )
      : {};
  };

  return (
    <Stack>
      <Title order={3}>Yeni iş izni talebi</Title>
      <Text size="sm" c="dimmed">
        Talep TASLAK olarak açılır. Gönderdiğinizde onay zinciri çözülür ve uygunluk kontrolü kalem
        kalem çalışır — engelleyen kalem varsa izin sahaya çıkmaz.
      </Text>

      <Card withBorder>
        <Stack>
          <Group grow align="start">
            <Select
              label="Şirket"
              data={(sirketler.data ?? []).map((s) => ({ value: s.id, label: s.ad }))}
              value={companyId}
              onChange={(deger) => {
                setCompanyId(deger);
                setWorkplaceId(null);
                setLocationId(null);
              }}
              data-testid="talep-sirket"
              searchable
            />
            <Select
              label="İşyeri"
              data={isyerleri.map((w) => ({ value: w.id, label: w.ad }))}
              value={workplaceId}
              onChange={(deger) => {
                setWorkplaceId(deger);
                setLocationId(null);
                setSorumluId(null);
                // Havuzlar işyerine bağlıdır: işyeri değişince eski seçimler
                // başka bir işyerinin kayıtlarına işaret ederdi.
                setEkipmanIds([]);
                setKatilimcilar([]);
              }}
              data-testid="talep-isyeri"
              disabled={companyId === null}
              searchable
            />
          </Group>

          <QueryStatus queries={[secenekler]} />

          <Select
            label="Lokasyon"
            description="İzin lokasyona bağlanır; onay havuzu bu lokasyonu kapsayan bahçelerden çözülür."
            data={(veri?.lokasyonlar ?? []).map((l) => ({
              value: l.id,
              // Girinti ağaç derinliğidir — sunucu türetir, kayıtta kolon yok.
              label: `${'— '.repeat(l.seviye)}${l.ad}`,
            }))}
            value={locationId}
            onChange={setLocationId}
            data-testid="talep-lokasyon"
            disabled={workplaceId === null}
            searchable
          />

          <Group grow>
            <TextInput
              label="Geçerlilik başlangıcı"
              type="datetime-local"
              value={baslangic}
              onChange={(e) => setBaslangic(e.currentTarget.value)}
              data-testid="talep-baslangic"
            />
            <TextInput
              label="Geçerlilik bitişi"
              type="datetime-local"
              value={bitis}
              onChange={(e) => setBitis(e.currentTarget.value)}
              data-testid="talep-bitis"
            />
          </Group>

          <MultiSelect
            label="İzin türleri"
            description="Tür ÇOKLUDUR (bir iş hem sıcak iş hem kapalı alan girişi olabilir)."
            data={(veri?.izinTurleri ?? []).map((t) => ({ value: t.id, label: t.ad }))}
            value={turIds}
            onChange={setTurIds}
            data-testid="talep-turler"
            disabled={workplaceId === null}
            searchable
          />

          <Select
            label="Güncel sorumlu"
            description="Sahada işbaşını, devri ve kapanış bildirimini o yürütür — gönderimde zorunlu."
            data={(veri?.sorumluAdaylari ?? []).map((s) => ({
              value: s.employmentId,
              label: `${kisiEtiketi(s.personAd, s.sahaGirisiGecerli)} — ${s.isverenAd}`,
            }))}
            value={etkinSorumluId}
            onChange={setSorumluId}
            data-testid="talep-sorumlu"
            disabled={workplaceId === null}
            searchable
          />

          <Checkbox
            label="Tüm holdingdeki kişileri göster"
            description="Kapalıyken kişi listeleri bu işyerinde çalışanlarla sınırlıdır. Başka işyerinden destek çağırıyorsanız açın."
            checked={tumHolding}
            onChange={(olay) => setTumHolding(olay.currentTarget.checked)}
            data-testid="talep-tum-holding"
            disabled={workplaceId === null}
          />

          <MultiSelect
            label="İş ekipmanları"
            description="Yalnız periyodik kontrol marjını geçen ekipman listelenir — marjı dolan ekipman burada GÖRÜNMEZ."
            data={(veri?.ekipmanlar ?? []).map((e) => ({ value: e.equipmentId, label: e.ad }))}
            value={ekipmanIds}
            onChange={setEkipmanIds}
            data-testid="talep-ekipman"
            disabled={workplaceId === null}
            searchable
          />

          <KatilimciBolumu
            adaylar={veri?.katilimciAdaylari ?? []}
            gorevler={veri?.gorevler ?? []}
            katilimcilar={katilimcilar}
            yeniKisi={yeniKisi}
            yeniGorev={yeniGorev}
            setYeniKisi={setYeniKisi}
            setYeniGorev={setYeniGorev}
            onEkle={() => {
              if (!yeniKisi || !yeniGorev) return;
              const varMi = katilimcilar.some(
                (k) => k.personId === yeniKisi && k.gorevRefId === yeniGorev,
              );
              if (!varMi)
                setKatilimcilar([...katilimcilar, { personId: yeniKisi, gorevRefId: yeniGorev }]);
              setYeniKisi(null);
            }}
            onSil={(index) => setKatilimcilar(katilimcilar.filter((_, i) => i !== index))}
          />

          {/* TÜRE ÖZEL FORM ALANLARI (✏️ S8-1, F4·S8): şablon TALEP ekranının
              İÇİNDE dolar — ayrı sayfaya/Formlar modülüne yönlendirme YOK.
              Alanlar FORM MOTORUNDAN gelir (Kapsam §22.5 + §19/12-f); ekran
              elle alan EKLEMEZ ve KURAL yazmaz — zorunluluk denetimi talep
              GÖNDERİMİNDE sunucuda koşar (S8-3), taslak kilitsizdir. */}
          <IzinTuruFormGirisleri
            turSecili={turIds.length > 0}
            formlar={turFormlari}
            cevaplar={formCevaplari}
            digerMetinleri={formDigerleri}
            onCevaplar={(formTemplateId, yeni, yeniDiger) => {
              setFormCevaplari((mevcut) => ({ ...mevcut, [formTemplateId]: yeni }));
              setFormDigerleri((mevcut) => ({ ...mevcut, [formTemplateId]: yeniDiger }));
            }}
          />

          {/* ✏️ GEZİNTİ ARTÇISI 2 / B2.3 — İTA BU EKRANDA GÖRÜNÜR, DETAYDA
              DOLDURULUR. Satır izin kimliği ister; iki yol tartıldı:
              (a) satırları burada biriktirip taslak kaydından SONRA yazmak
              (tür formu deseni) · (b) bölümü görünür kılıp detaya bırakmak.
              (b) SEÇİLDİ: tür formunun kayıp penceresini gönderim kapısı
              yakalar (zorunlu alan), İTA'nınkini HİÇBİR ŞEY yakalamaz —
              zorunluluk yok (§16.7.4/6-3); taslak açılıp İTA yazılamazsa
              analiz sessizce kaybolur ve boş İTA onaya gider. Tek yazma yolu
              detaydaki satırdır; kullanıcı "İTA nerede" diye aramaz çünkü
              bölüm burada adıyla durur ve kayıttan sonra detay açılır.
              ✏️ GEZİNTİ ARTÇISI 3 / B6: bölüm TÜR FORMUNDAN SONRA durur
              (genelden özele — ürün sahibi 08.09.2026). */}
          <Stack gap={2} data-testid="talep-ita-bolumu">
            <Text fw={600}>İş tehlike analizi (İTA)</Text>
            <Text size="xs" c="dimmed" data-testid="talep-ita-bilgi">
              İzni taslak olarak kaydettikten sonra doldurulur.
            </Text>
          </Stack>

          {talepAc.isError && (
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="talep-hata">
              {talepAc.error.message}
            </Text>
          )}

          <Group>
            <Button
              data-testid="talep-taslak-kaydet"
              loading={talepAc.isPending}
              disabled={eksik}
              onClick={() => {
                talepAc.mutate(
                  {
                    workplaceId: workplaceId!,
                    locationId: locationId!,
                    gecerlilikBaslangic: new Date(baslangic).toISOString(),
                    gecerlilikBitis: new Date(bitis).toISOString(),
                    izinTuruRefIds: turIds,
                    katilimcilar,
                    ekipmanIds,
                    guncelSorumluEmploymentId: etkinSorumluId,
                  },
                  {
                    // Taslak açıldıktan sonra iş DETAY sayfasında sürer:
                    // gönderim, uygunluk kalemleri ve iptal orada — talep
                    // formunun ikinci bir kopyası tutulmaz.
                    onSuccess: (ozet) => {
                      // S8-1: tür formları izinle BİRLİKTE taslak kayda döner
                      // (izne bağlı, form motorunun kendi ucu). Bir kayıt
                      // yazılamazsa yolculuk durmaz — form izin detayında da
                      // doldurulabilir; gönderim kapısı (S8-3) eksik bırakmaz.
                      void (async () => {
                        const doldurmaTarihi = sahaGunu.data;
                        if (!doldurmaTarihi) return;
                        for (const form of turFormlari.data ?? []) {
                          try {
                            await formKayitAc.mutateAsync({
                              formTemplateId: form.formTemplateId,
                              workplaceId: workplaceId!,
                              doldurmaTarihi,
                              cevaplar: formCevaplari[form.formTemplateId] ?? {},
                              notlar: {},
                              digerMetinleri: digerGovdesi(form),
                              taslak: true,
                              ptwPermitId: ozet.id,
                            });
                          } catch {
                            // bilinçli: detayda tamamlanır (S8-3 son kapıdır)
                          }
                        }
                        navigate(`/izinler/${ozet.id}`);
                      })();
                    },
                  },
                );
              }}
            >
              Taslağı kaydet
            </Button>
            {taslakEksikleri.length > 0 ? (
              <Text size="xs" c="dimmed" data-testid="talep-taslak-sebep">
                Taslak için {listeMetni(taslakEksikleri)} gerekli.
              </Text>
            ) : digerEksigi !== null ? (
              <Text size="xs" c="dimmed" data-testid="talep-taslak-sebep">
                {digerEksigi}
              </Text>
            ) : (
              <Text size="xs" c="dimmed">
                Kaydettikten sonra izin detayında gönderebilir, uygunluk sonucunu görebilir ve
                gerekirse iptal edebilirsiniz.
              </Text>
            )}
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * KATILIMCI = (KİŞİ, GÖREV) ÇİFTİ — kişi TEK BAŞINA eklenemez (Kapsam
 * §19/11-3). Kural UI'da da görünür: iki seçim dolmadan "ekle" çalışmaz.
 * Gerekçe kuralın kendisidir — yetkinlik şartı izne değil izin içi GÖREVE
 * asılıdır; görevsiz katılımcının hangi belgesinin aranacağı bilinemez.
 */
function KatilimciBolumu({
  adaylar,
  gorevler,
  katilimcilar,
  yeniKisi,
  yeniGorev,
  setYeniKisi,
  setYeniGorev,
  onEkle,
  onSil,
}: {
  adaylar: PtwKisiAdayi[];
  gorevler: { id: string; ad: string; varsayilan: boolean }[];
  katilimcilar: { personId: string; gorevRefId: string }[];
  yeniKisi: string | null;
  yeniGorev: string | null;
  setYeniKisi: (deger: string | null) => void;
  setYeniGorev: (deger: string | null) => void;
  onEkle: () => void;
  onSil: (index: number) => void;
}): React.JSX.Element {
  const adMap = new Map(adaylar.map((a) => [a.personId, a.ad]));
  const gorevMap = new Map(gorevler.map((g) => [g.id, g.ad]));
  const varsayilanGorev = gorevler.find((g) => g.varsayilan);

  return (
    <Stack gap="xs">
      <Text fw={600}>Katılımcılar</Text>
      <Text size="xs" c="dimmed">
        Katılımcı kişi + GÖREV çiftidir; yetkinlik şartı göreve asılıdır. Görev seçilmeden katılımcı
        eklenemez.
        {varsayilanGorev ? ` Taban görev: ${varsayilanGorev.ad}.` : ''}
      </Text>
      <Group align="end">
        <Select
          label="Kişi"
          data={adaylar.map((a) => ({
            value: a.personId,
            label: kisiEtiketi(a.ad, a.sahaGirisiGecerli),
          }))}
          value={yeniKisi}
          onChange={setYeniKisi}
          data-testid="talep-katilimci-kisi"
          searchable
          w={240}
        />
        <Select
          label="Görev"
          data={gorevler.map((g) => ({ value: g.id, label: g.ad }))}
          value={yeniGorev}
          onChange={setYeniGorev}
          data-testid="talep-katilimci-gorev"
          searchable
          w={240}
        />
        <Button
          variant="default"
          data-testid="talep-katilimci-ekle"
          disabled={!yeniKisi || !yeniGorev}
          onClick={onEkle}
        >
          Ekle
        </Button>
      </Group>
      {katilimcilar.length > 0 && (
        <Table data-testid="talep-katilimci-listesi">
          <Table.Tbody>
            {katilimcilar.map((k, index) => (
              <Table.Tr key={`${k.personId}-${k.gorevRefId}`}>
                <Table.Td>{adMap.get(k.personId) ?? k.personId}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{gorevMap.get(k.gorevRefId) ?? k.gorevRefId}</Badge>
                </Table.Td>
                <Table.Td w={90}>
                  <Button
                    size="xs"
                    variant="subtle"
                    color={ANLAM.engel}
                    onClick={() => onSil(index)}
                  >
                    Çıkar
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function varsayilanBaslangic(): string {
  return datetimeLocalDegeri(new Date(Date.now() + 60 * 60_000));
}

function varsayilanBitis(): string {
  return datetimeLocalDegeri(new Date(Date.now() + 9 * 60 * 60_000));
}

/** "a", "a ve b", "a, b ve c" — KA-8 sebep satırı. */
function listeMetni(adlar: readonly string[]): string {
  if (adlar.length <= 1) return adlar.join('');
  return `${adlar.slice(0, -1).join(', ')} ve ${adlar[adlar.length - 1]}`;
}
